import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Injectable, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { Observable, shareReplay } from 'rxjs';
import { ProductsService } from '../../order/product.service';
import { Products } from '../../order/models/product.model';

@Injectable({ providedIn: 'root' })
export class AccountingCatalog {
    private catalogs = new Map<number, Observable<Products[]>>();
    constructor(private products: ProductsService) {}
    load(companyId: number): Observable<Products[]> {
        if (!this.catalogs.has(companyId)) this.catalogs.set(companyId, this.products.getSharedCatalog(companyId).pipe(shareReplay({ bufferSize: 1, refCount: true })));
        return this.catalogs.get(companyId)!;
    }
}

@Component({
    selector: 'app-accounting-catalog-description', standalone: true,
    imports: [CommonModule, FormsModule, AutoCompleteModule, ButtonModule],
    template: `<p-autoComplete [ngModel]="choice" (ngModelChange)="change($event)" [suggestions]="suggestions"
        (completeMethod)="search($event.query)" (onSelect)="select($event.value)" optionLabel="description"
        [forceSelection]="false" [dropdown]="true" [disabled]="disabled" [tabindex]="tabindex" [inputId]="inputId" appendTo="body" styleClass="w-full" inputStyleClass="w-full"
        [placeholder]="'Search catalog or enter a description'">
        <ng-template pTemplate="item" let-product><strong>{{ product.productCode }}</strong> — {{ product.description || product.name }}</ng-template>
    </p-autoComplete>
    <small *ngIf="productId" class="catalog-link">{{ productCode || 'Catalog item' }}
        <p-button icon="pi pi-times" [text]="true" [disabled]="disabled" size="small" ariaLabel="Use custom description" (onClick)="clearCatalog.emit()"></p-button>
    </small>`,
    styles: [':host { display:block; min-width:0; } .catalog-link { display:flex; align-items:center; gap:.25rem; }']
})
export class AccountingCatalogDescriptionComponent implements OnChanges {
    @Input() companyId?: number;
    @Input() disabled = false;
    @Input() tabindex = 0;
    @Input() inputId?: string;
    @Input() description = '';
    @Input() productId?: number;
    @Input() productCode?: string;
    @Output() descriptionChange = new EventEmitter<string>();
    @Output() productSelected = new EventEmitter<Products>();
    @Output() clearCatalog = new EventEmitter<void>();
    choice: Products | string = '';
    suggestions: Products[] = [];
    private catalog: Products[] = [];
    constructor(private catalogs: AccountingCatalog) {}
    ngOnChanges(changes: SimpleChanges): void {
        this.choice = this.description;
        if (!changes['companyId']) return;
        const companyId = this.companyId;
        this.catalog = [];
        if (companyId) this.catalogs.load(companyId).subscribe({
            next: rows => { if (companyId === this.companyId) this.catalog = rows.filter(p => !p.deletedAt && p.owningCompanyId === companyId); },
            error: () => { if (companyId === this.companyId) this.catalog = []; }
        });
    }
    change(value: Products | string): void { this.choice = value; if (typeof value === 'string') this.descriptionChange.emit(value); }
    select(product: Products): void { this.productSelected.emit(product); }
    search(query: string): void {
        const term = (query || '').toLowerCase();
        this.suggestions = this.catalog.filter(p => `${p.productCode} ${p.name} ${p.description}`.toLowerCase().includes(term)).slice(0, 50);
    }
}
